import Parse from 'parse'; // TODO: provide via service

import { inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Analyst, IState } from '@analysis/domain';
import type { AnalystRepository, AnalysisStateRepository, FetchSampleAnalystsUseCase } from '@analysis/use-cases';
import { AnalyzerEntity } from '@telespot/domain';
import { EMPTY, from, Observable, of } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ANALYSIS_STATE_SERVICE, AnalysisStateStore } from '../analysis-state';

interface AnalystState extends EntityState<Analyst> {
  selectedAnalyst: string;
  error: string | undefined;
  loading: boolean;
  /**
   * Analyst lists of OTHER samples, for the breadcrumb cascade. Kept apart from the
   * entity map, which belongs to the open sample: `setAll`-ing a foreign sample's
   * analysts there would clobber the current selection's list.
   *
   * `null` = a fetch is pending; a missing key = never requested.
   */
  analystsBySampleId: Record<string, Analyst[] | null>;
  /**
   * The sample `resolveAnalystForSample` last completed for. Lets consumers tell "the
   * selected analyst belongs to the sample on screen" from "it is still the previous
   * sample's analyst", without inferring it from the timing of the `loading` flag.
   */
  resolvedSampleId: string | undefined;
}

const analystAdapter = createEntityAdapter<Analyst>({ selectId: (analyst) => analyst.id });

const initialState = {
  ...analystAdapter.getInitialState(),
  selectedAnalyst: undefined,
  error: undefined,
  loading: false,
  analystsBySampleId: {},
  resolvedSampleId: undefined,
};

export const ANALYST_SERVICE = new InjectionToken<AnalystRepository>('ANALYST_SERVICE');
export const FETCH_SAMPLE_ANALYSTS_USE_CASE = new InjectionToken<FetchSampleAnalystsUseCase>(
  'FETCH_SAMPLE_ANALYSTS_USE_CASE'
);

@Injectable()
export class AnalystStore extends ComponentStore<AnalystState> {
  private readonly analystRepository = inject<AnalystRepository>(ANALYST_SERVICE);
  private readonly fetchSampleAnalystsUseCase = inject<FetchSampleAnalystsUseCase>(FETCH_SAMPLE_ANALYSTS_USE_CASE);
  private readonly analysisStateStore = inject(AnalysisStateStore);
  private readonly analysisStateApi = inject<AnalysisStateRepository>(ANALYSIS_STATE_SERVICE);

  constructor() {
    super(initialState);
  }

  // #region Selectors

  public readonly selectedAnalyst$ = this.select((state) => state.entities[state.selectedAnalyst]).pipe(
    distinctUntilChanged((a, b) => a?.id === b?.id)
  );
  public readonly selectedAnalystIsCurrentUser$ = this.select(
    this.selectedAnalyst$,
    (selectedAnalyst) => selectedAnalyst?.id === Parse.User.current()?.id
  );
  // Tracks the historyId of the currently-selected analyst as it lives in the
  // store's entity map. Reads from raw state instead of selectedAnalyst$ so
  // that re-emissions are NOT gated by the id-based distinctUntilChanged —
  // the same analyst across two samples has a different historyId per sample,
  // and this selector must surface that change so the URL can be kept in sync.
  public readonly selectedAnalystHistoryId$ = this.select((state) => state.entities[state.selectedAnalyst]?.historyId);
  public readonly error$ = this.select((state) => state.error);
  public readonly analysts$ = this.select((state) => Object.values(state.entities));
  public readonly loading$ = this.select((state) => state.loading);
  /** Analyst lists of other samples, for the breadcrumb cascade. */
  public readonly analystsBySampleId$ = this.select((state) => state.analystsBySampleId);
  /** The sample the current selection was resolved for. */
  public readonly resolvedSampleId$ = this.select((state) => state.resolvedSampleId);

  // Filter analysts by type
  public readonly userAnalysts$ = this.select(this.analysts$, (analysts) =>
    analysts.filter((analyst) => analyst?.type === 'user')
  );

  public readonly aiAlgorithms$ = this.select(this.analysts$, (analysts) =>
    analysts.filter((analyst) => analyst?.type === 'algorithm')
  );

  // Reviewed analysts (user analysts excluding current user)
  public readonly reviewedAnalyst$ = this.select(this.userAnalysts$, (userAnalysts) =>
    userAnalysts.filter((a) => a?.id !== Parse.User.current()?.id)
  );

  public readonly analystsExcludingCurrentUser$ = this.select(this.analysts$, (analysts) =>
    analysts.filter((a) => a?.id !== Parse.User.current()?.id)
  );

  public readonly analystChanged$ = this.selectedAnalyst$.pipe(
    startWith(undefined),
    pairwise(),
    filter(([prev, curr]) => !!curr && prev !== curr),
    map(([, curr]) => curr)
  );

  public readonly isReviewingOtherAnalyst$ = this.select(this.selectedAnalyst$, (selectedAnalyst) =>
    selectedAnalyst ? selectedAnalyst.id !== Parse.User.current()?.id : false
  );

  // #endregion

  // #region Updaters

  public readonly selectAnalyst = this.updater((state, analystId: string) => ({
    ...state,
    selectedAnalyst: analystId,
  }));

  public readonly addAnalyst = this.updater((state, analyst: Analyst) => ({
    ...analystAdapter.upsertOne(analyst, state),
  }));

  // #endregion

  // #region Effects

  public readonly getCurrentAnalyst = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      concatMap((sampleId) => {
        const currentUser = Parse.User.current();
        if (!currentUser) return EMPTY;

        return from(this.analysisStateApi.getUserState(currentUser.id, null, sampleId)).pipe(
          switchMap((analysisState) =>
            analysisState
              ? of(analysisState)
              : from(
                  this.analysisStateApi.saveAnalysisState(
                    {
                      id: undefined,
                      analystId: currentUser.id,
                      analystEntity: 'user' as AnalyzerEntity,
                      state: IState.READ,
                      reference: false,
                      bookmarkAssetId: undefined,
                      favouriteAssetIds: [],
                    },
                    sampleId,
                    null
                  )
                )
          ),
          tap((analysisState) => {
            this.analysisStateStore.updateAnalysisState(analysisState);
            const analyst: Analyst = {
              id: analysisState.analystId,
              historyId: analysisState.id,
              type: (analysisState.analystEntity as 'user' | 'algorithm') ?? 'user',
              name: analysisState.name ?? (currentUser.get('username') as string) ?? '',
            };
            this.patchState((state) => ({
              ...analystAdapter.upsertOne(analyst, state),
              selectedAnalyst: analyst.id,
            }));
          }),
          catchError((error) => {
            this.patchState({ error: error.message });
            return EMPTY;
          })
        );
      })
    )
  );

  public readonly getHistory = this.effect((historyId$: Observable<string>) =>
    historyId$.pipe(
      concatMap((historyId) =>
        from(this.analystRepository.getHistory(historyId)).pipe(
          filter((analyst): analyst is Analyst => !!analyst),
          tap((analyst) =>
            this.patchState((state) => ({
              ...analystAdapter.addOne(analyst, state),
              selectedAnalyst: analyst.id,
            }))
          ),
          catchError((error) => {
            this.patchState({ error: error.message });
            return EMPTY;
          })
        )
      )
    )
  );

  /**
   * Analysts of ANY sample, for the breadcrumb cascade. Writes only to
   * `analystsBySampleId` — never to the entity map or the selection — and skips a
   * sample it already knows, so it is safe to call on every hover.
   */
  public readonly loadAnalystsForSampleId = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      filter((sampleId) => !!sampleId),
      concatLatestFrom(() => this.analystsBySampleId$),
      filter(([sampleId, bySampleId]) => bySampleId[sampleId] === undefined),
      tap(([sampleId]) => this.patchAnalystsForSample(sampleId, null)),
      mergeMap(([sampleId]) =>
        from(this.fetchSampleAnalystsUseCase.execute({ sampleId })).pipe(
          catchError(() => of([] as Analyst[])),
          tap((analysts) => this.patchAnalystsForSample(sampleId, analysts))
        )
      )
    )
  );

  /** Synchronous read of a cascaded sample's analysts, for resolving a commit. */
  public analystsForSample(sampleId: string): Analyst[] | null | undefined {
    return this.get((state) => state.analystsBySampleId[sampleId]);
  }

  private patchAnalystsForSample(sampleId: string, analysts: Analyst[] | null): void {
    this.patchState((state) => ({
      analystsBySampleId: { ...state.analystsBySampleId, [sampleId]: analysts },
    }));
  }

  public readonly loadAnalysts = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      tap(() => this.patchState({ loading: true, error: undefined })),
      concatMap((sampleId) =>
        from(this.fetchSampleAnalystsUseCase.execute({ sampleId })).pipe(
          concatLatestFrom(() => this.selectedAnalyst$),
          tap(([analysts, selectedAnalyst]) => {
            // Replace the entity map with the fresh list for THIS sample.
            const full =
              selectedAnalyst && !analysts.some((a) => a.id === selectedAnalyst.id)
                ? [...analysts, selectedAnalyst]
                : analysts;
            this.patchState((state) => ({
              ...analystAdapter.setAll(full, state),
              loading: false,
            }));
          }),
          catchError((error) => {
            this.patchState({ error: error.message, loading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Loads the analyst list for the sample AND resolves which analyst should be
  // selected. Selection priority:
  //   1. `preferredAnalystId` — an analyst the user picked EXPLICITLY for this sample
  //      (the breadcrumb commits sample and analyst together). It outranks everything,
  //      including the preserve rule below, which would otherwise override a
  //      deliberate switch back to one's own analysis.
  //   2. `preferredHistoryId` — the URL's historyId param (for deep links /
  //      reloads / share-a-link). If an analyst in the sample's list matches,
  //      that wins. This is the *only* signal the store has about the URL.
  //   3. Current store selection (analyst chosen previously in this session,
  //      if their id still appears in the destination sample — preserves
  //      cross-sample review).
  //   4. Fallback to the current user (creating the analysisState if missing,
  //      same as getCurrentAnalyst). Also covers an explicit pick of an analyst who
  //      has no analysis on this sample yet — which is the normal case for oneself.
  public readonly resolveAnalystForSample = this.effect(
    (params$: Observable<{ sampleId: string; preferredHistoryId?: string; preferredAnalystId?: string }>) =>
      params$.pipe(
        tap(() => this.patchState({ loading: true, error: undefined })),
        concatMap(({ sampleId, preferredHistoryId, preferredAnalystId }) =>
          from(this.fetchSampleAnalystsUseCase.execute({ sampleId })).pipe(
            concatLatestFrom(() => this.selectedAnalyst$),
            concatMap(([analysts, currentSelection]) => {
              const explicit = preferredAnalystId
                ? analysts.find((a) => a.id === preferredAnalystId)
                : undefined;
              const fromUrl = preferredHistoryId ? analysts.find((a) => a.historyId === preferredHistoryId) : undefined;
              // An explicit pick that isn't in the list can only be the current user
              // (their analysis state may not exist yet) — the fallback branch creates it.
              const preserved =
                explicit ??
                fromUrl ??
                (!preferredAnalystId && currentSelection && analysts.find((a) => a.id === currentSelection.id));

              if (preserved) {
                return from(this.analysisStateApi.getUserState(preserved.id, null, sampleId)).pipe(
                  tap((analysisState) => {
                    if (analysisState) {
                      this.analysisStateStore.updateAnalysisState(analysisState);
                    }
                    this.patchState((state) => ({
                      ...analystAdapter.setAll(analysts, state),
                      loading: false,
                      selectedAnalyst: preserved.id,
                      resolvedSampleId: sampleId,
                    }));
                  })
                );
              }

              const currentUser = Parse.User.current();
              if (!currentUser) {
                this.patchState((state) => ({
                  ...analystAdapter.setAll(analysts, state),
                  loading: false,
                  selectedAnalyst: undefined,
                  resolvedSampleId: sampleId,
                }));
                return EMPTY;
              }

              return from(this.analysisStateApi.getUserState(currentUser.id, null, sampleId)).pipe(
                switchMap((analysisState) =>
                  analysisState
                    ? of(analysisState)
                    : from(
                        this.analysisStateApi.saveAnalysisState(
                          {
                            id: undefined,
                            analystId: currentUser.id,
                            analystEntity: 'user' as AnalyzerEntity,
                            state: IState.READ,
                            reference: false,
                            bookmarkAssetId: undefined,
                            favouriteAssetIds: [],
                          },
                          sampleId,
                          null
                        )
                      )
                ),
                tap((analysisState) => {
                  this.analysisStateStore.updateAnalysisState(analysisState);
                  const fallbackAnalyst: Analyst = {
                    id: analysisState.analystId,
                    historyId: analysisState.id,
                    type: (analysisState.analystEntity as 'user' | 'algorithm') ?? 'user',
                    name: analysisState.name ?? (currentUser.get('username') as string) ?? '',
                  };
                  const fullList = analysts.some((a) => a.id === fallbackAnalyst.id)
                    ? analysts
                    : [...analysts, fallbackAnalyst];
                  this.patchState((state) => ({
                    ...analystAdapter.setAll(fullList, state),
                    loading: false,
                    selectedAnalyst: fallbackAnalyst.id,
                    resolvedSampleId: sampleId,
                  }));
                })
              );
            }),
            catchError((error) => {
              this.patchState({ error: error.message, loading: false });
              return EMPTY;
            })
          )
        )
      )
  );

  public readonly reloadAnalysts = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      tap(() => this.patchState({ loading: true, error: undefined })),
      concatMap((sampleId) =>
        from(this.fetchSampleAnalystsUseCase.execute({ sampleId })).pipe(
          tap({
            next: (analysts) =>
              this.patchState((state) => ({
                ...analystAdapter.setAll(analysts, state),
                loading: false,
                selectedAnalyst: undefined,
              })),
            error: (error) => this.patchState({ error: error.message, loading: false }),
          })
        )
      )
    )
  );

  // #endregion
}
